import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, IconButton, Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import Steps from "app/pages/sales-intelligence/common-components/steps/Steps/Steps";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { isSignalTypeKey } from "app/pages/sales-intelligence/domain/signals/utils/signal-key";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { LEAD_SCORE_TYPE_CONFIG } from "app/pages/sales-intelligence/modules/lead-scoring-agent/configuration/score-type";
import { useScoringFiltersConfig } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringFiltersConfig";
import { useSyncAutoAppliedCreatedSignalToPanelValue } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useSyncAutoAppliedCreatedSignalToPanelValue";
import {
    panelValueToScoringRule,
    scoringRuleToPanelValue,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringRuleAdapters";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import { resolveLeadsFilterPanelValue } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/resolveLeadsFilterPanelValue";
import { useAllRawUISignalsGroupedById } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useUISignals";
import {
    type IScoringRuleBusinessContextDto,
    type IScoringRuleDto,
    ScoringConfigType,
} from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";
import { translateSignalsToMapById } from "app/pages/sales-intelligence/sub-modules/signals/utils/translateSignalsToMapById";

import type { ScoringRuleWizardMode } from "./resolveInitialTeamIds";
import { Step1FiltersAndSignals } from "./steps/Step1FiltersAndSignals";
import { Step2BusinessContext } from "./steps/Step2BusinessContext";
import { Step3TeamSelection } from "./steps/Step3TeamSelection";
import {
    SCORING_RULE_MODAL_STYLES,
    StyledScoringWizardBody,
    StyledScoringWizardFooter,
    StyledScoringWizardHeader,
    StyledScoringWizardStepperRow,
} from "./styles";
import { useScoringRuleTeamScope } from "./useScoringRuleTeamScope";

type WizardStepId = "filters" | "context" | "team";

const WIZARD_STEP_LABEL_KEYS: Record<WizardStepId, string> = {
    filters: "si.pages.score_settings.wizard.steps.filters",
    context: "si.pages.score_settings.wizard.steps.context",
    team: "si.pages.score_settings.wizard.steps.team",
};

const WIZARD_STEP_IDS: readonly WizardStepId[] = ["filters", "context", "team"];

export type ScoringRuleWizardModalProps = {
    isOpen: boolean;
    mode: ScoringRuleWizardMode;
    priority: LeadPriorityScore;
    configType: ScoringConfigType;
    initialRule?: IScoringRuleDto;
    /** Teams preselected on the team step of a new rule, so a rule created from a team page applies to it. */
    defaultTeamIds?: readonly number[];
    onClose: () => void;
    onSave: (rule: IScoringRuleDto) => void;
    isSaving?: boolean;
};

const EMPTY_PANEL: Partial<LeadsSearchAllFiltersTypes> = {};
const EMPTY_CONTEXT: IScoringRuleBusinessContextDto = {};
const EMPTY_TEAM_IDS: readonly number[] = [];

const ScoringRuleWizardModalBody = (props: ScoringRuleWizardModalProps) => {
    const {
        mode,
        priority,
        configType,
        initialRule,
        defaultTeamIds = EMPTY_TEAM_IDS,
        onClose,
        onSave,
        isSaving,
    } = props;
    const { translate } = useTranslate();

    const showTeamStep = configType === ScoringConfigType.Account;

    const rawUISignals = useAllRawUISignalsGroupedById();
    const signalById = useMemo(() => translateSignalsToMapById(Object.values(rawUISignals)), [rawUISignals]);

    const [step, setStep] = useState<WizardStepId>("filters");
    const [panelValue, setPanelValue] = useState<Partial<LeadsSearchAllFiltersTypes>>(() =>
        initialRule ? scoringRuleToPanelValue(initialRule, signalById) : EMPTY_PANEL,
    );
    const [businessContext, setBusinessContext] = useState<IScoringRuleBusinessContextDto>(
        initialRule?.businessContext ?? EMPTY_CONTEXT,
    );
    const teamScope = useScoringRuleTeamScope({ mode, initialRule, defaultTeamIds, enabled: showTeamStep });

    const filtersConfig = useScoringFiltersConfig();
    const { filtersList } = filtersConfig;

    const typedFiltersList = filtersList as unknown as readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];

    const handlePanelValueUpdate = useCallback((next: Partial<LeadsSearchAllFiltersTypes>) => {
        setPanelValue((current) => resolveLeadsFilterPanelValue(current, next));
    }, []);

    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: panelValue,
        filtersConfig: typedFiltersList,
        onFiltersUpdate: handlePanelValueUpdate,
    });

    useSyncAutoAppliedCreatedSignalToPanelValue({
        panelValue,
        onPanelValueUpdate: handlePanelValueUpdate,
    });

    const hasSelectedSignals = Object.entries(panelValue).some(
        ([key, value]) => isSignalTypeKey(key) && Array.isArray(value) && value.length > 0,
    );

    const isStep1Empty = filtersManager.getDirtyFilters().length === 0 && !hasSelectedSignals;
    const trimmedContext = (businessContext.translation ?? "").trim();
    const isStep2Invalid = trimmedContext.length === 0;

    const priorityLabel = translate(LEAD_SCORE_TYPE_CONFIG[priority]?.labelKey ?? "");
    const titleKey =
        mode === "edit" ? "si.pages.score_settings.wizard.title.edit" : "si.pages.score_settings.wizard.title.new";

    const wizardStepIds = showTeamStep ? WIZARD_STEP_IDS : WIZARD_STEP_IDS.filter((id) => id !== "team");
    const wizardSteps = wizardStepIds.map((id) => WIZARD_STEP_LABEL_KEYS[id]);
    const currentStepIndex = wizardStepIds.indexOf(step);

    const handleNext = () => {
        if (step === "filters") {
            setStep("context");
        } else if (step === "context" && showTeamStep) {
            setStep("team");
        }
    };

    const handleBack = () => {
        if (step === "context") {
            setStep("filters");
        } else if (step === "team") {
            setStep("context");
        }
    };

    const isLastStep = showTeamStep ? step === "team" : step === "context";

    const handleSubmit = () => {
        const rule = panelValueToScoringRule({
            panelValue,
            businessContext,
            existingId: initialRule?.id,
        });
        const teamIds = showTeamStep ? teamScope.teamIdsForPayload : undefined;

        onSave(teamIds ? { ...rule, teamIds } : rule);
    };

    return (
        <>
            <StyledScoringWizardHeader>
                <Text fontVariant="h3">{translate(titleKey, { priority: priorityLabel })}</Text>
                <IconButton
                    dataAutomation="scoring-rule-wizard.close"
                    iconName="clear"
                    iconSize="sm"
                    type="flat"
                    onClick={onClose}
                />
            </StyledScoringWizardHeader>

            <StyledScoringWizardStepperRow data-automation="scoring-rule-wizard.stepper">
                <Steps
                    currentStep={currentStepIndex}
                    steps={wizardSteps}
                />
            </StyledScoringWizardStepperRow>

            <StyledScoringWizardBody>
                {step === "filters" ? (
                    <Step1FiltersAndSignals
                        panelValue={panelValue}
                        onPanelValueUpdate={handlePanelValueUpdate}
                        filtersList={filtersList}
                        filtersSearchConfigList={filtersConfig.filtersSearchConfigList}
                        categoriesList={filtersConfig.categoriesList}
                        status={filtersConfig.status}
                    />
                ) : step === "context" ? (
                    <Step2BusinessContext
                        value={businessContext}
                        onChange={setBusinessContext}
                    />
                ) : (
                    <Step3TeamSelection
                        teams={teamScope.teams}
                        scope={teamScope.scope}
                        selectedTeamIds={teamScope.selectedTeamIds}
                        onSelectScope={teamScope.selectScope}
                        onToggleTeam={teamScope.toggleTeam}
                    />
                )}
            </StyledScoringWizardBody>

            <StyledScoringWizardFooter>
                <div />
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    {step !== "filters" ? (
                        <Button
                            type="flat"
                            onClick={handleBack}
                            dataAutomation="scoring-rule-wizard.back"
                        >
                            {translate("si.common.button.back")}
                        </Button>
                    ) : null}
                    {isLastStep ? (
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            isDisabled={isStep2Invalid || isSaving || (showTeamStep && !teamScope.isValid)}
                            isLoading={isSaving}
                            dataAutomation="scoring-rule-wizard.save"
                        >
                            {translate("si.common.button.save")}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            onClick={handleNext}
                            isDisabled={step === "filters" ? isStep1Empty : isStep2Invalid}
                            dataAutomation="scoring-rule-wizard.next"
                        >
                            {translate("si.common.button.next")}
                        </Button>
                    )}
                </Flex>
            </StyledScoringWizardFooter>
        </>
    );
};

export const ScoringRuleWizardModal = (props: ScoringRuleWizardModalProps) => {
    const { isOpen, onClose } = props;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={SCORING_RULE_MODAL_STYLES}
            data-automation="scoring-rule-wizard"
        >
            {isOpen ? (
                <ScoringRuleWizardModalBody
                    key={`${props.mode}-${props.priority}-${props.configType}`}
                    {...props}
                />
            ) : null}
        </Modal>
    );
};
